// =============================================================================
// Admin Settings Page - Viewers Tab
// =============================================================================

import { useState, useEffect } from 'react';
import {
  Cog6ToothIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { adminService } from '../../services/admin.service';
import type { TeamMemberResponse, TeamAccessLevel } from '../../services/admin.service';
import { ViewerModal } from './components/ViewerModal';
import { DeleteViewerModal } from './components/DeleteViewerModal';
import { useAuth } from '../../hooks/useAuth';

// =============================================================================
// Access Level Styling
// =============================================================================

const accessLevelConfig: Record<TeamAccessLevel, { label: string; color: string; bg: string }> = {
  FULL_ACCESS: { label: 'Full Access', color: 'text-emerald-700', bg: 'bg-emerald-50 ring-emerald-600/20' },
  ASSET_MANAGER: { label: 'Asset Manager', color: 'text-blue-700', bg: 'bg-blue-50 ring-blue-600/20' },
  INVESTOR_RELATIONS: { label: 'Investor Relations', color: 'text-purple-700', bg: 'bg-purple-50 ring-purple-600/20' },
  DEAL_ACCESS: { label: 'Deal Access', color: 'text-amber-700', bg: 'bg-amber-50 ring-amber-600/20' },
  VIEW_ONLY: { label: 'View Only', color: 'text-gray-700', bg: 'bg-gray-50 ring-gray-600/20' },
};

// =============================================================================
// Component
// =============================================================================

export function SettingsPage() {
  const { isAdmin } = useAuth();
  const [teamMembers, setTeamMembers] = useState<TeamMemberResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMemberResponse | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingMember, setDeletingMember] = useState<TeamMemberResponse | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  // Fetch team members
  const fetchTeamMembers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const members = await adminService.listTeamMembers();
      setTeamMembers(members);
    } catch (err) {
      console.error('Failed to fetch team members:', err);
      setError('Failed to load team members');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  // Feedback state
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Auto-dismiss success feedback after 4 seconds
  useEffect(() => {
    if (feedback?.type === 'success') {
      const timer = setTimeout(() => setFeedback(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  // Handle resend invitation
  const handleResendInvitation = async (member: TeamMemberResponse) => {
    try {
      setResendingId(member.id);
      setFeedback(null);
      await adminService.resendTeamInvitation(member.id);
      setFeedback({ type: 'success', message: `Invitation resent to ${member.email}` });
    } catch (err) {
      console.error('Failed to resend invitation:', err);
      setFeedback({ type: 'error', message: 'Failed to resend invitation. Please try again.' });
    } finally {
      setResendingId(null);
    }
  };

  // Handle delete
  const handleDeleteConfirmed = async () => {
    if (!deletingMember) return;
    try {
      setFeedback(null);
      await adminService.deleteTeamMember(deletingMember.id);
      setShowDeleteModal(false);
      setDeletingMember(null);
      setFeedback({ type: 'success', message: 'Team member removed successfully' });
      fetchTeamMembers();
    } catch (err) {
      console.error('Failed to delete team member:', err);
      setShowDeleteModal(false);
      setDeletingMember(null);
      setFeedback({ type: 'error', message: 'Failed to remove team member. Please try again.' });
    }
  };

  // Handle modal close with refresh
  const handleModalClose = (saved: boolean) => {
    setShowViewerModal(false);
    setEditingMember(null);
    if (saved) {
      fetchTeamMembers();
    }
  };

  // Admin-only guard: TEAM users cannot manage other team viewers
  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <Cog6ToothIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">Settings are only accessible to administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-100">
            <Cog6ToothIcon className="h-5 w-5 text-primary-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-sm text-gray-500">Manage team viewers and portal access</p>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button className="border-b-2 border-primary-500 py-3 px-1 text-sm font-medium text-primary-600">
            Viewers
          </button>
        </nav>
      </div>

      {/* Feedback Banner */}
      {feedback && (
        <div className={`rounded-lg p-3 mb-4 flex items-center justify-between ${
          feedback.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          <p className="text-sm">{feedback.message}</p>
          <button onClick={() => setFeedback(null)} className="text-sm font-medium hover:opacity-70">Dismiss</button>
        </div>
      )}

      {/* Viewers Content */}
      <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
            <p className="text-sm text-gray-500">
              {teamMembers.length} {teamMembers.length === 1 ? 'viewer' : 'viewers'} with admin portal access
            </p>
          </div>
          <button
            onClick={() => {
              setEditingMember(null);
              setShowViewerModal(true);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Add Viewer
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-sm text-red-600">{error}</p>
            <button
              onClick={fetchTeamMembers}
              className="mt-2 text-sm text-primary-600 hover:text-primary-700"
            >
              Try again
            </button>
          </div>
        ) : teamMembers.length === 0 ? (
          <div className="text-center py-16">
            <Cog6ToothIcon className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-3 text-sm font-semibold text-gray-900">No team viewers</h3>
            <p className="mt-1 text-sm text-gray-500">
              Add team members to give them access to the admin portal with specific permissions.
            </p>
            <div className="mt-6">
              <button
                onClick={() => {
                  setEditingMember(null);
                  setShowViewerModal(true);
                }}
                className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
              >
                <PlusIcon className="h-4 w-4" />
                Add Your First Viewer
              </button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {/* Table Header */}
            <div className="hidden sm:grid sm:grid-cols-12 gap-4 px-6 py-3 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">Email</div>
              <div className="col-span-2">Access Level</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2">Last Login</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>

            {/* Rows */}
            {teamMembers.map((member) => {
              const levelConfig = accessLevelConfig[member.accessLevel];
              const hasLoggedIn = !!member.lastLoginAt;
              const isPending = !hasLoggedIn;

              return (
                <div
                  key={member.id}
                  className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 px-6 py-4 hover:bg-gray-50 transition-colors items-center"
                >
                  {/* Name */}
                  <div className="sm:col-span-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-semibold text-primary-700">
                          {(member.firstName?.[0] || '').toUpperCase()}
                          {(member.lastName?.[0] || '').toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{member.name}</p>
                        {member.assignedDeals.length > 0 && (
                          <p className="text-xs text-gray-500 truncate">
                            {member.assignedDeals.length} {member.assignedDeals.length === 1 ? 'deal' : 'deals'} assigned
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="sm:col-span-3">
                    <p className="text-sm text-gray-600 truncate">{member.email}</p>
                  </div>

                  {/* Access Level Badge */}
                  <div className="sm:col-span-2">
                    <span
                      className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${levelConfig.bg} ${levelConfig.color}`}
                    >
                      {levelConfig.label}
                    </span>
                  </div>

                  {/* Status */}
                  <div className="sm:col-span-1">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${isPending ? 'text-amber-600' : member.isActive ? 'text-green-600' : 'text-gray-400'}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${isPending ? 'bg-amber-500' : member.isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                      {isPending ? 'Pending' : member.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {/* Last Login */}
                  <div className="sm:col-span-2">
                    <p className="text-sm text-gray-500">
                      {member.lastLoginAt
                        ? new Date(member.lastLoginAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : 'Never'}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="sm:col-span-1 flex items-center justify-end gap-1">
                    {isPending && (
                      <button
                        onClick={() => handleResendInvitation(member)}
                        disabled={resendingId === member.id}
                        title="Resend invitation"
                        aria-label="Resend invitation"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50"
                      >
                        {resendingId === member.id ? (
                          <ArrowPathIcon className="h-4 w-4 animate-spin" />
                        ) : (
                          <EnvelopeIcon className="h-4 w-4" />
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setEditingMember(member);
                        setShowViewerModal(true);
                      }}
                      title="Edit permissions"
                      aria-label="Edit permissions"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        setDeletingMember(member);
                        setShowDeleteModal(true);
                      }}
                      title="Remove viewer"
                      aria-label="Remove viewer"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit Viewer Modal */}
      {showViewerModal && (
        <ViewerModal
          member={editingMember}
          onClose={handleModalClose}
        />
      )}

      {/* Delete Viewer Modal */}
      {showDeleteModal && deletingMember && (
        <DeleteViewerModal
          memberName={deletingMember.name}
          onConfirm={handleDeleteConfirmed}
          onCancel={() => {
            setShowDeleteModal(false);
            setDeletingMember(null);
          }}
        />
      )}
    </div>
  );
}
